import type { flagPartData } from "@/api/flaggedPartService";
import { useCheckVersionPart } from "@/hooks/flaggedPartHooks";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import DescriptionIcon from "@mui/icons-material/Description";
import { Link as RouterLink } from "react-router-dom";
import { useMemo } from "react";

interface BreakdownLinkButtonProps {
  partId: number;
  supplierCode: string;
}

const BreakdownLinkButton = ({ partId, supplierCode }: BreakdownLinkButtonProps) => {
  const { data, isLoading } = useCheckVersionPart(partId);
  const version = data?.version ?? "v2";

  return (
    <IconButton
      component={RouterLink}
      to={`/breakdown/${supplierCode}/${version}/${partId}`}
      disabled={isLoading}
    >
      <DescriptionIcon color="primary" />
    </IconButton>
  );
};

interface FlaggedPartTableProps {
  rows: flagPartData[];
  count: number;
  isLoading: boolean;
  page: number;
  rowPerPage: number;
  onPageChange: (_event: unknown, newPage: number) => void;
  onRowPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setSelectedParts: React.Dispatch<
    React.SetStateAction<
      {
        partId: number;
        approvalId?: string;
      }[]
    >
  >;
  selectedParts: {
    partId: number;
    approvalId?: string;
  }[];
  handleUnFlag: (partId?: number, approvalId?: string) => void;
  canEdit: boolean;
}

const FlaggedPartTable = ({
  rows,
  count,
  isLoading,
  page,
  rowPerPage,
  onPageChange,
  onRowPerPageChange,
  setSelectedParts,
  selectedParts,
  handleUnFlag,
  canEdit,
}: FlaggedPartTableProps) => {

  const dedupedRows = useMemo(() => {
    const seen = new Set<number>();
    return rows.filter((row) => {
      if (seen.has(row.part_id)) return false;
      seen.add(row.part_id);
      return true;
    });
  }, [rows]);

  const isAllSelected = useMemo(() => {
    if (dedupedRows.length === 0) return false;
    return dedupedRows.every((row) =>
      selectedParts.some((sel) => sel.partId === row.part_id),
    );
  }, [dedupedRows, selectedParts]);

  const handleSelectOne = (
    partId: number,
    approvalId: string,
    checked: boolean,
  ) => {
    if (checked) {
      setSelectedParts((prev) => [...prev, { partId, approvalId }]);
    } else {
      setSelectedParts((prev) => prev.filter((sel) => sel.partId !== partId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedParts(
        dedupedRows.map((row) => ({
          partId: row.part_id,
          approvalId: row.approval_id,
        })),
      );
    } else {
      setSelectedParts([]);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          minHeight: "400px",
          overflow: "auto",
          position: "relative",
          width: "100%",
          backgroundColor: "#f3f3f3ff",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "auto" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 103,
                  width: 70,
                  minWidth: 70,
                  maxWidth: 70,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  color="warning"
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 70,
                  top: 0,
                  zIndex: 103,
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                Action
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 190,
                  top: 0,
                  zIndex: 103,
                  width: 150,
                  minWidth: 150,
                  maxWidth: 150,
                  textAlign: "left",
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Part No.
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 340,
                  top: 0,
                  zIndex: 103,
                  width: 200,
                  minWidth: 200,
                  maxWidth: 200,
                  textAlign: "left",
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Part Name
              </TableCell>
              <TableCell
                sx={{
                  zIndex: 102,
                  minWidth: 150,
                  textAlign: "left",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                1st Tier Supplier
              </TableCell>
              <TableCell
                sx={{
                  zIndex: 102,
                  minWidth: 200,
                  textAlign: "left",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                Material Supplier
              </TableCell>
              <TableCell
                sx={{
                  zIndex: 102,
                  minWidth: 350,
                  textAlign: "left",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                Material Code
              </TableCell>
              <TableCell
                sx={{
                  zIndex: 102,
                  minWidth: 150,
                  textAlign: "left",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                Buyer ID
              </TableCell>
              <TableCell
                sx={{
                  zIndex: 102,
                  minWidth: 350,
                  textAlign: "left",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                Reason
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={4}>
                      <Skeleton variant="text" height={25} animation="wave" />
                    </TableCell>
                    <TableCell colSpan={4}>
                      <Skeleton variant="text" height={25} animation="wave" />
                    </TableCell>
                    <TableCell colSpan={1}>
                      <Skeleton variant="text" height={25} animation="wave" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : (
              dedupedRows.map((row) => (
                <TableRow key={row.part_id}>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 101,
                      width: 70,
                      minWidth: 70,
                      maxWidth: 70,
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    <Checkbox
                      checked={selectedParts?.some(
                        (sel) => sel.partId === row.part_id,
                      )}
                      onChange={(e) =>
                        handleSelectOne(
                          row.part_id,
                          row.approval_id,
                          e.target.checked,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 70,
                      zIndex: 101,
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {row.is_flagged && (
                          <Tooltip
                            title={"Flag"}
                            placement="top"
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: "offset",
                                    options: { offset: [0, -10] },
                                  },
                                ],
                              },
                            }}
                          >
                            <IconButton
                              disabled={!canEdit}
                              onClick={() =>
                                handleUnFlag(row.part_id, row.approval_id)
                              }
                            >
                              <FlagIcon
                                sx={{
                                  color: row.is_admin_flagged
                                    ? "success.light"
                                    : "error.main",
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      <Box
                        sx={{
                          width: 40,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip
                          title={"รายละเอียด"}
                          placement="top"
                          slotProps={{
                            popper: {
                              modifiers: [
                                { name: "offset", options: { offset: [0, -10] } },
                              ],
                            },
                          }}
                        >
                          <BreakdownLinkButton
                            partId={row.part_id}
                            supplierCode={row.first_tier_supplier_code}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 190,
                      zIndex: 101,
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "left",
                      backgroundColor: "#f5f5f5",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    {row.part_number}
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 340,
                      zIndex: 101,
                      width: 200,
                      minWidth: 200,
                      maxWidth: 200,
                      textAlign: "left",
                      backgroundColor: "#f5f5f5",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    {row.part_name}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "left",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    {row.first_tier_supplier}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "left",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    {row.material_supplier}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 350,
                      minWidth: 350,
                      maxWidth: 350,
                      textAlign: "left",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    <Tooltip title={row.material_code || ""} placement="top">
                      <Typography
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        fontSize="0.875rem"
                      >
                        {row.material_code || ""}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "left",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      {row.buyer_id?.split(",")?.map((buyerId: string) => {
                        if (!buyerId.trim()) return null;
                        return (
                          <Chip
                            key={buyerId}
                            color="primary"
                            label={buyerId.trim()}
                            sx={{ mr: 1 }}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 350,
                      minWidth: 350,
                      maxWidth: 350,
                      textAlign: "left",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    <Tooltip title={row.flagged_reason || ""} placement="top">
                      <Typography
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        fontSize="0.875rem"
                      >
                        {row.flagged_reason || ""}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
            {!isLoading && dedupedRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{ height: 350, verticalAlign: "middle", fontWeight: 500 }}
                >
                  ไม่มีข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowPerPage}
        onRowsPerPageChange={onRowPerPageChange}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Box>
  );
};

export default FlaggedPartTable;
